import WelcomeContent from '../shared/WelcomeContent';
import WelcomeImage from '../shared/WelcomeImage';
import { displayLabel } from "../../utils/displayLabel";
// components/public/WelcomeReader.js
import React, { useEffect, useMemo, useState } from "react";

import { getAnchorProps } from "../../utils/linkProps";
import ReaderPageLayout from "../layouts/ReaderPageLayout";

const WELCOME_API =
  "https://v70pmk2q7a.execute-api.us-west-2.amazonaws.com/default/get-welcome";
const welcomeSections = value => (Array.isArray(value) ? value : [])
  .filter(section => section && typeof section === 'object')
  .map(section => ({
    title: typeof section.title === 'string' ? section.title : '',
    text: typeof section.text === 'string' ? section.text : '',
    imageUrl: typeof section.imageUrl === 'string' && /^https?:\/\//i.test(section.imageUrl) ? section.imageUrl : '',
  }));

export default function WelcomeReader({
  authorName: authorNameProp,
  penName: penNameProp,
  welcomeHeader: welcomeHeaderProp,
  tagline: taglineProp,
  welcomeHtml: welcomeHtmlProp,
  ctas: ctasProp,
  profileImageUrl: profileImageUrlProp,
  sections: sectionsProp,
  contentFormat: contentFormatProp,
}) {
  const hasAllProps = useMemo(() => {
    const contentProvided =
      !!authorNameProp ||
      !!welcomeHeaderProp ||
      !!taglineProp ||
      !!welcomeHtmlProp ||
      !!profileImageUrlProp;
    return contentProvided && Array.isArray(ctasProp);
  }, [
    authorNameProp,
    welcomeHeaderProp,
    taglineProp,
    welcomeHtmlProp,
    profileImageUrlProp,
    ctasProp,
  ]);

  const [loading, setLoading] = useState(!hasAllProps);
  const [retry, setRetry] = useState(0);
  const [error, setError] = useState("");

  const [authorName, setAuthorName] = useState(authorNameProp || "");
  const [penName, setPenName] = useState(penNameProp || "");
  const [welcomeHeader, setWelcomeHeader] = useState(welcomeHeaderProp || "");
  const [tagline, setTagline] = useState(taglineProp || "");
  const [welcomeHtml, setWelcomeHtml] = useState(welcomeHtmlProp || "");
  const [ctas, setCtas] = useState(Array.isArray(ctasProp) ? ctasProp : []);
  const [profileImageUrl, setProfileImageUrl] = useState(profileImageUrlProp || "");
  const [sections, setSections] = useState(welcomeSections(sectionsProp));
  const [contentFormat, setContentFormat] = useState(contentFormatProp || 'legacy');

  useEffect(() => {
    if (hasAllProps) {
      setAuthorName(authorNameProp || ''); setPenName(penNameProp || '');
      setWelcomeHeader(welcomeHeaderProp || ''); setTagline(taglineProp || '');
      setWelcomeHtml(welcomeHtmlProp || ''); setProfileImageUrl(profileImageUrlProp || '');
      setCtas(ctasProp || []); setSections(welcomeSections(sectionsProp));
      setContentFormat(contentFormatProp || 'legacy'); setError(''); setLoading(false);
      return;
    }
    const controller = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setError("");

        const host = typeof window !== "undefined" ? window.location.host : "";
        const res = await fetch(`${WELCOME_API}?host=${encodeURIComponent(host)}`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`Welcome API ${res.status}`);

        const data = await res.json();

        if (controller.signal.aborted) return;
        if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('Invalid welcome page');
        setAuthorName(''); setPenName(typeof data.penName === 'string' ? data.penName : '');
        setWelcomeHeader(typeof data.welcomeHeader === 'string' ? data.welcomeHeader : '');
        setTagline(typeof data.tagline === 'string' ? data.tagline : '');
        setWelcomeHtml(typeof data.welcomeMessage === 'string' ? data.welcomeMessage : '');
        setProfileImageUrl(typeof data.profileImageUrl === 'string' ? data.profileImageUrl : '');
        setCtas(Array.isArray(data.ctaButtons) ? data.ctaButtons : []);
        setContentFormat(data.contentFormat === 'text' ? 'text' : 'legacy');
        setSections(welcomeSections(data.sections));
      } catch (e) {
        if (controller.signal.aborted) return;
        setWelcomeHeader(''); setTagline(''); setWelcomeHtml(''); setProfileImageUrl('');
        setAuthorName(''); setPenName(''); setCtas([]); setSections([]);
        setError('We could not load this Welcome Page. Please retry.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [hasAllProps, retry, authorNameProp, penNameProp, welcomeHeaderProp, taglineProp, welcomeHtmlProp, profileImageUrlProp, ctasProp, sectionsProp, contentFormatProp]);

  const pageTitle = loading ? "" : welcomeHeader;

  return (
    <ReaderPageLayout
      title={pageTitle}
      description={loading ? "" : tagline}
      centeredHeader
      contentClassName="space-y-6"
    >
        {!loading && <div className="text-center"><a href="/bookstore" className="public-button">Browse books →</a></div>}
      <div className="bg-white rounded-2xl shadow-md p-6 space-y-6">
        {loading ? (
          <div className="space-y-6 animate-pulse">
            <div className="h-28 w-28 rounded-full bg-gray-200 mx-auto" />
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded w-full" />
              <div className="h-4 bg-gray-200 rounded w-11/12" />
              <div className="h-4 bg-gray-200 rounded w-10/12" />
            </div>
          </div>
        ) : (
          <>
            {profileImageUrl && (
              <div className="flex justify-center">
                <WelcomeImage
                  src={profileImageUrl}
                  alt={displayLabel(penName || authorName)}
                  className="w-28 h-28 sm:w-32 sm:h-32 rounded-full object-cover border border-bookish-royal shadow-sm"
                  loading="lazy"
                />
              </div>
            )}

            {welcomeHtml && <WelcomeContent value={welcomeHtml} format={contentFormat} />}

            {!welcomeHtml && !error && (
              <p className="text-sm sm:text-base text-gray-700 text-center">
                Welcome to this author&apos;s Circle.
              </p>
            )}

            {error && <div className="text-sm text-bookish-purple text-center"><p role="alert">{error}</p><button type="button" className="underline min-h-[44px]" onClick={() => setRetry(value => value + 1)}>Retry loading</button></div>}
          </>
        )}
      </div>

      {!loading && sections.map((section, index) => <section key={index} className="bg-white rounded-2xl shadow-md p-6 space-y-4">
        {section.title && <h2 className="font-serif text-2xl text-bookish-royal">{section.title}</h2>}
        {section.imageUrl && <WelcomeImage src={section.imageUrl} alt={section.title || ''} className="max-w-full rounded-xl mx-auto" loading="lazy" />}
        {section.text && <p className="whitespace-pre-wrap text-gray-700">{section.text}</p>}
      </section>)}

      {!loading && Array.isArray(ctas) && ctas.length > 0 && (
        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="flex flex-wrap gap-3 justify-center">
            {ctas.map((btn, i) => {
              const anchorProps = getAnchorProps(
                btn?.url,
                typeof window !== "undefined" ? window.location.host : ""
              );

              return (
                <a
                  key={`${btn?.label || "cta"}-${i}`}
                  {...anchorProps}
                  href={anchorProps.href || "#"}
                  className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
                    i === 0
                      ? "bg-bookish-purple text-white hover:bg-bookish-purple/90"
                      : "bg-white text-bookish-purple border border-bookish-purple/20 hover:border-bookish-purple/40"
                  }`}
                >
                  {btn?.label || "Learn More"}
                </a>
              );
            })}
          </div>
        </div>
      )}
    </ReaderPageLayout>
  );
}
